import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { MovementFlow, OutcomeBars } from "../components/MovementViz";
import { MotionGallery } from "../components/MotionGallery";
import { PROGRAMS } from "../data/content";
import { SCHEDULER_URL, ASSETS } from "../lib/constants";

const ACCENT = "#c46a58";
const PALS_VIDEO = "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/sqmwfuml_CB10F213-E348-49E3-AC90-CC5DD2111E9A.MP4";

const DEV_PILLARS = [
  { title: "Emotional Regulation", desc: "Building empathy, self-control, and healthy interactions through shared musical experiences." },
  { title: "Central Nervous System (CNS) Regulation", desc: "Steady, repetitive rhythm calms the nervous system — easing stress and helping little bodies feel settled, safe, and ready to learn." },
  { title: "Frontal Lobe Activation & Executive Functioning", desc: "Enhancing focus, memory, and problem-solving through rhythmic patterns and challenges." },
  { title: "Problem-Solving & Adaptive Thinking", desc: "Encouraging flexibility and resilience as children learn to respond to new rhythms and musical cues." },
  { title: "Language Development & Processing", desc: "Stimulating verbal and non-verbal communication skills through call-and-response and expressive play." },
];

const MOVES = [
  { move: "Cross-Body Drumming", badge: "Cross-Midline Coordination", benefit: "Reaching across the body boosts left/right brain connectivity." },
  { move: "Clapping Patterns", badge: "Fine & Gross Motor Control", benefit: "Builds spatial awareness, precision, and hand-eye coordination." },
  { move: "Bilateral Tapping", badge: "Rhythmic Synchronization", benefit: "Supports language acquisition and active listening skills." },
];

const PILLARS = [
  { label: "Language", pct: 92, note: "Call-and-response stimulates verbal & non-verbal communication." },
  { label: "Social / Emotional Regulation", pct: 88, note: "Shared play builds empathy, self-control, healthy interaction." },
  { label: "Cognitive Development", pct: 85, note: "Rhythmic patterns sharpen focus, memory, and problem-solving." },
  { label: "Fine Motor Skills", pct: 80, note: "Drumming technique develops precise, coordinated movement." },
  { label: "Adaptability", pct: 76, note: "Responding to new rhythms grows flexibility and resilience." },
];

const GALLERY = [
  { image: "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/37lm0vl3_casey-with-kids%20pp.webp", label: "Joyful play" },
  { image: "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/74zlsrd7_kid%20-%20pp.webp", label: "Little learners" },
  { image: "https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/jaauohqr_480230809_680594620958100_1730755669174107948_n.jpg", label: "Rhythm & connection" },
];

export default function PercussionPals() {
  const prog = PROGRAMS.find((p) => p.slug === "percussion-pals");
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="grain min-h-screen" data-testid="page-percussion-pals">
      <Nav />
      <section className="relative pt-36 md:pt-48 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `radial-gradient(90% 70% at 20% 0%, rgba(196,106,88,0.16), transparent 60%)` }} />
        <div className="relative max-w-6xl mx-auto">
          <Link to="/#programs" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-fg/50 hover:text-fg transition-colors mb-10"><ArrowLeft size={14} /> All Programs</Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <img src="/percussion-pals-logo.png" alt="Percussion Pals" data-testid="pals-hero-logo" className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover mb-6 shadow-[0_10px_40px_rgba(196,106,88,0.28)]" />
              <p className="font-mono text-xs tracking-[0.3em] uppercase mb-4" style={{ color: ACCENT }}>For Early Childhood Education</p>
              <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Percussion Pals</h1>
              <p className="mt-6 max-w-xl text-fg/70 md:text-xl leading-relaxed">Rhythm-based learning that builds school readiness — strong minds, calm bodies, and connected hearts, through joyful movement.</p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href={SCHEDULER_URL} target="_blank" rel="noopener noreferrer" data-testid="pals-book-top" className="shape-r font-medium px-7 py-3.5 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>Book a Session</a>
                <a href="#pals-resource" className="shape-r border border-white/25 font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">Request program details</a>
              </div>
            </div>
            <div className="shape-r overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid="pals-hero-sizzle">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/37lm0vl3_casey-with-kids%20pp.webp"
                alt="Casey with young learners"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Motor Skill Movement Flow</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-14">How movement builds the brain.</h2>
          <MovementFlow steps={MOVES} accent={ACCENT} testid="pals-movement-flow" />
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5 overflow-hidden" data-testid="pals-pillars-section">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>The Science of Play</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4 max-w-3xl">The 5 pillars of early childhood development.</h2>
          <p className="text-fg/60 md:text-lg max-w-2xl mb-12">Every Percussion Pals session is designed around the developmental foundations that early learners are building — one joyful beat at a time.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="shape-r overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.4)]" data-testid="pals-pillars-video">
              <video className="w-full aspect-video object-cover" autoPlay muted loop playsInline preload="auto" poster={ASSETS.heroFallback}>
                <source src={PALS_VIDEO} type="video/mp4" />
              </video>
            </div>
            <ol className="space-y-3.5" data-testid="pals-pillars-list">
              {DEV_PILLARS.map((p, i) => (
                <motion.li
                  key={p.title}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.45, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="glass shape-r flex items-start gap-4 p-4 md:p-5 border border-white/10"
                  data-testid={`pals-pillar-${i + 1}`}
                >
                  <span
                    className="shrink-0 grid place-items-center h-11 w-11 rounded-full font-serif text-lg mt-0.5"
                    style={{ backgroundColor: `${ACCENT}1F`, border: `1.5px solid ${ACCENT}`, color: ACCENT }}
                  >
                    {i + 1}
                  </span>
                  <span>
                    <span className="block font-medium text-[15px] md:text-lg leading-snug">{p.title}</span>
                    <span className="block text-fg/60 text-sm md:text-[15px] leading-snug mt-1">{p.desc}</span>
                  </span>
                </motion.li>
              ))}
            </ol>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 shape-r glass border border-white/10 p-6 md:p-8 font-serif text-xl md:text-2xl leading-snug"
            data-testid="pals-pillars-closing"
          >
            These pillars are not theoretical. They are <span style={{ color: ACCENT }}>observable inside classrooms every day.</span>
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>In The Classroom</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-5">Active bodies, joyful play.</h2>
            <p className="text-fg/65 md:text-lg leading-relaxed">Delivered as short 15–20 minute micro-experiences tuned to early-childhood attention spans — multiple classes in one visit.</p>
          </div>
          <div className="shape-r overflow-hidden border border-white/10" data-testid="pals-video">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_trauma-healing-tech/artifacts/74zlsrd7_kid%20-%20pp.webp"
              alt="A young learner drumming in class"
              className="w-full aspect-video object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Movement-Based Outcomes</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">Developmental milestones we grow.</h2>
          <p className="text-fg/55 text-sm mb-10">Illustrative emphasis across our foundational pillars, aligned with early-childhood standards.</p>
          <OutcomeBars items={PILLARS} accent={ACCENT} testid="pals-outcomes" />
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs tracking-[0.3em] uppercase mb-3" style={{ color: ACCENT }}>Moments From The Room</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-3">Joy, in motion.</h2>
          <p className="text-fg/55 text-sm mb-10 max-w-xl">Real classrooms, real smiles — a glimpse of Percussion Pals in action.</p>
        </div>
        <MotionGallery items={GALLERY} accent={ACCENT} testid="pals-gallery" />
      </section>

      <section id="pals-resource" className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-3" style={{ color: ACCENT }}><Sparkles size={16} /><p className="font-mono text-xs tracking-[0.3em] uppercase">Free Download</p></div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-4">The ECE Music Activity Guide.</h2>
          <p className="text-fg/60 md:text-lg mb-8">Pick your age group and we'll send a printable guide of rhythm games you can lead tomorrow.</p>
          <LeadForm
            testid="pals-download-form" type="download" page="percussion-pals" accent={ACCENT}
            submitLabel="Send my guide" showMessage={false}
            selects={[{ name: "age_group", label: "Age group", options: ["Infants & toddlers", "Ages 3–5", "Mixed early years"] }]}
            downloadUrl="/casey-ece-activity-guide.pdf"
            successTitle="Your guide is ready." successBody="Tap below to download — a copy is on its way to your inbox too."
          />
        </div>
      </section>

      <section className="py-20 md:py-28 px-6 md:px-10 border-t border-white/5 text-center">
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Bring Percussion Pals to your classrooms.</h2>
        <a href={SCHEDULER_URL} target="_blank" rel="noopener noreferrer" data-testid="pals-book-bottom" className="inline-flex items-center gap-2 mt-8 shape-r font-medium px-8 py-4 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT, color: "#141b1d" }}>Book a Session <ArrowRight size={17} /></a>
      </section>
      <Footer />
    </div>
  );
}
